/**
 * File upload dropzone for the project documents tab (§7g Commit F).
 *
 * Wraps react-dropzone so we get keyboard accessibility, nested-child
 * dragleave handling, and paste-from-clipboard support for free. File
 * type filtering matches the backend's allowed extensions from 7b; a
 * file outside the allowlist shows an inline rejection toast.
 *
 * Upload orchestration lives in store.uploadDocument which handles
 * the three-step pre-signed URL flow (file PUT → metadata PUT → sync).
 * This component just hands files to the store and displays in-flight
 * state.
 *
 * Per §7.8 Q9: no indexing status UI. On success we push a toast
 * explaining the KB sync delay, then forget about it.
 */

import { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useStore } from "../store/useStore.js";

// Matches the ALLOWED_EXTENSIONS set in backend/functions/projects/app.py (7b).
const ACCEPT = {
  "text/markdown": [".md"],
  "text/plain": [".txt"],
  "application/pdf": [".pdf"],
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [
    ".docx",
  ],
  "audio/mpeg": [".mp3"],
  "audio/wav": [".wav"],
  "audio/mp4": [".m4a"],
  "video/mp4": [".mp4"],
};

// 100MB cap — generous enough for a long audio recording, tight enough
// that a huge upload doesn't hang the pre-signed PUT forever.
const MAX_SIZE_BYTES = 100 * 1024 * 1024;

export default function DropzoneArea({ projectId, compact = false }) {
  const uploadDocument = useStore((s) => s.uploadDocument);
  const pushToast = useStore((s) => s.pushToast);

  const [uploading, setUploading] = useState(false);
  const [uploadingFilename, setUploadingFilename] = useState(null);

  const onDrop = useCallback(
    async (acceptedFiles, fileRejections) => {
      // Surface rejections first so the user sees why a file was skipped.
      fileRejections.forEach(({ file, errors }) => {
        const reason = errors[0]?.message || "unsupported file";
        pushToast(`${file.name}: ${reason}`, "error");
      });

      if (acceptedFiles.length === 0) return;

      setUploading(true);
      // Upload sequentially so we can surface per-file progress via toasts.
      for (const file of acceptedFiles) {
        setUploadingFilename(file.name);
        try {
          await uploadDocument(projectId, file);
          pushToast(
            `${file.name} added. It may take a minute before it appears in chat responses.`,
            "success",
          );
        } catch (err) {
          pushToast(`${file.name} failed: ${err.message}`, "error");
        }
      }
      setUploading(false);
      setUploadingFilename(null);
    },
    [projectId, uploadDocument, pushToast],
  );

  const { getRootProps, getInputProps, isDragActive, isDragReject } =
    useDropzone({
      onDrop,
      accept: ACCEPT,
      maxSize: MAX_SIZE_BYTES,
      disabled: uploading,
    });

  const state = uploading
    ? "uploading"
    : isDragReject
      ? "reject"
      : isDragActive
        ? "active"
        : "idle";

  if (compact) {
    return (
      <div
        {...getRootProps({
          className: `upload-bar upload-bar--${state}`,
        })}
      >
        <input {...getInputProps()} />
        <span className="upload-bar__button">
          {uploading ? "Uploading…" : "Upload files"}
        </span>
        <span className="upload-bar__hint">
          {uploading
            ? uploadingFilename
            : isDragReject
              ? "File type not supported"
              : isDragActive
                ? "Drop to upload"
                : "or drag and drop — .md, .txt, .pdf, .docx, audio, video · max 100MB"}
        </span>
      </div>
    );
  }

  return (
    <div {...getRootProps({ className: `dropzone dropzone-${state}` })}>
      <input {...getInputProps()} />
      {uploading ? (
        <div className="dropzone-message">
          <span className="dropzone-title">
            Uploading {uploadingFilename}…
          </span>
          <span className="dropzone-hint">
            This can take a moment for large files. Don&apos;t close the tab.
          </span>
        </div>
      ) : isDragReject ? (
        <div className="dropzone-message">
          <span className="dropzone-title">File type not supported</span>
          <span className="dropzone-hint">
            Accepted: .md, .txt, .pdf, .docx, .mp3, .mp4, .wav, .m4a
          </span>
        </div>
      ) : isDragActive ? (
        <div className="dropzone-message">
          <span className="dropzone-title">Drop to upload</span>
        </div>
      ) : (
        <div className="dropzone-message">
          <span className="dropzone-title">
            Drag files here or click to browse
          </span>
          <span className="dropzone-hint">
            .md, .txt, .pdf, .docx, .mp3, .mp4, .wav, .m4a · max 100MB
          </span>
        </div>
      )}
    </div>
  );
}
